"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { ref, get, update } from "../lib/firebase-database"
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "../lib/firebase-storage"
import { db, storage } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Textarea } from "./ui/textarea"
import { Alert, AlertDescription, AlertTitle } from "./ui/alert"
import { MaterialIcon } from "./MaterialIcon"
import { X } from 'lucide-react'

const facilityOptions = [
  { id: "wifi", label: "WiFi", icon: () => <MaterialIcon name="wifi" /> },
  { id: "tv", label: "TV", icon: () => <MaterialIcon name="tv" /> },
  { id: "ac", label: "AC", icon: () => <MaterialIcon name="ac_unit" /> },
  { id: "kitchen", label: "Kitchen", icon: () => <MaterialIcon name="kitchen" /> },
  { id: "parking", label: "Parking", icon: () => <MaterialIcon name="local_parking" /> },
  { id: "petFriendly", label: "Pet Friendly", icon: () => <MaterialIcon name="pets" /> },
  { id: "pool", label: "Swimming Pool", icon: () => <MaterialIcon name="pool" /> },
  { id: "gym", label: "Gym", icon: () => <MaterialIcon name="fitness_center" /> },
  { id: "gamesRoom", label: "Games Room", icon: () => <MaterialIcon name="sports_esports" /> },
  { id: "garden", label: "Garden Area", icon: () => <MaterialIcon name="yard" /> },
  { id: "mountainView", label: "Mountain View", icon: () => <MaterialIcon name="landscape" /> },
  { id: "beachView", label: "Beach View", icon: () => <MaterialIcon name="beach_access" /> },
  { id: "alcoholAllowed", label: "Alcohol Allowed", icon: () => <MaterialIcon name="local_bar" /> },
  { id: "noDrinking", label: "No Drinking", icon: () => <MaterialIcon name="no_drinks" /> },
  { id: "birthday", label: "Birthday Celebrations", icon: () => <MaterialIcon name="cake" /> },
  { id: "party", label: "Party Celebrations", icon: () => <MaterialIcon name="celebration" /> },
  { id: "indoorGames", label: "Indoor Games", icon: () => <MaterialIcon name="sports_kabaddi" /> },
  { id: "bbq", label: "BBQ", icon: () => <MaterialIcon name="outdoor_grill" /> },
  { id: "meditation", label: "Meditation Area", icon: () => <MaterialIcon name="self_improvement" /> },
  { id: "spa", label: "Spa", icon: () => <MaterialIcon name="spa" /> },
  { id: "cottages", label: "Cottages", icon: () => <MaterialIcon name="cottage" /> },
  { id: "bungalow", label: "Bungalow", icon: () => <MaterialIcon name="foundation" /> },
  { id: "kingBed", label: "King Size Bed", icon: () => <MaterialIcon name="king_bed" /> },
  { id: "golfCourse", label: "Golf Course", icon: () => <MaterialIcon name="golf_course" /> },
  { id: "hiking", label: "Hiking", icon: () => <MaterialIcon name="hiking" /> },
  { id: "turf", label: "Turf", icon: () => <MaterialIcon name="sports_cricket" /> },
  { id: "cctv", label: "CCTV", icon: () => <MaterialIcon name="videocam" /> },
  { id: "powerBackup", label: "24/7 Power Backup", icon: () => <MaterialIcon name="bolt" /> },
  { id: "preWedding", label: "Pre-Wedding Shoot", icon: () => <MaterialIcon name="video_camera_front" /> },
  { id: "wedding", label: "Wedding", icon: () => <MaterialIcon name="face_3" /> },
  { id: "bigFunction", label: "Big Function", icon: () => <MaterialIcon name="groups" /> },
  { id: "towels", label: "Towels", icon: () => <MaterialIcon name="dry_cleaning" /> },
  { id: "dinner", label: "Dinner", icon: () => <MaterialIcon name="restaurant" /> },
].map((facility) => ({
  ...facility,
  icon: () => <div className="text-gray-600">{facility.icon()}</div>,
}))

export default function EditPropertyPage() {
  const initialFormData = {
    name: "",
    thumbnail: null,
    mainCity: "",
    address: "",
    description: "",
    architecturalUniqueness: "",
    guestCount: "",
    bedrooms: "",
    halls: "",
    kitchens: "",
    rate: "",
    facilities: [],
    images: [],
    locationMap: "",
    locationHighlights: [],
    management: [{ name: "", image: null }],
    owner: { name: "", image: null },
    youtubeLink: "",
    instagramProfile: "",
    whatsappNumber: "",
    totalVillaArea: "",
    ownerEmail: "",
    upiId: "",
    googleMapsLink: "",
    rules: [],
    trainDistance: "",
    busDistance: "",
    stationPickup: false,
    busStopPickup: false,
    locationImages: [],
    youtubeShorts: [],
    location: {
      lat: "",
      lng: "",
    },
    meals: {
      available: false,
      types: [],
      description: "",
      images: []
    },
    pricing: {
      enabled: false,
      categories: [
        { heading: "", price: "" }
      ],
      note: ""
    },
    poolSize: "",
    poolCount: "",
    checkInTime: "",
    checkOutTime: "",
    spaces: [],
    pricingType: "fullNight",
     perPersonGuestCount: "" 
  }

  const [formData, setFormData] = useState(initialFormData)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const { id } = useParams()

  useEffect(() => {
    const fetchVilla = async () => {
      try {
        const villaRef = ref(db, `villas/${id}`)
        const snapshot = await get(villaRef)
        if (snapshot.exists()) {
          const villaData = snapshot.val()
          if (!isAdmin && villaData.ownerEmail !== user?.email) {
            navigate("/")
            return
          }
          // Merge fetched data with initial state to ensure all fields are defined
          setFormData((prev) => ({
            ...initialFormData,
            ...villaData,
            // Ensure array fields are never null
            images: Array.isArray(villaData.images) ? villaData.images : [],
            facilities: Array.isArray(villaData.facilities)
              ? villaData.facilities.map(f => typeof f === 'string' ? { id: f, isPaid: false } : f)
              : [],
            locationHighlights: Array.isArray(villaData.locationHighlights) ? villaData.locationHighlights : [],
            locationImages: Array.isArray(villaData.locationImages) ? villaData.locationImages : [],
            youtubeShorts: Array.isArray(villaData.youtubeShorts) ? villaData.youtubeShorts : [],
            spaces: Array.isArray(villaData.spaces) ? villaData.spaces : [],
            rules: Array.isArray(villaData.rules) ? villaData.rules : [],
            meals: {
              ...initialFormData.meals,
              ...(villaData.meals || {}),
              images: Array.isArray(villaData.meals?.images) ? villaData.meals.images : [],
              types: Array.isArray(villaData.meals?.types) ? villaData.meals.types : [],
            },
            pricing: {
              ...initialFormData.pricing,
              ...(villaData.pricing || {}),
              categories: Array.isArray(villaData.pricing?.categories) ? villaData.pricing.categories : [{ heading: "", price: "" }],
            },
            location: {
              ...initialFormData.location,
              ...(villaData.location || {}),
            },
            owner: {
              ...initialFormData.owner,
              ...(villaData.owner || {}),
            },
            management: Array.isArray(villaData.management) ? villaData.management : (villaData.management ? [villaData.management] : initialFormData.management),
          }))
        } else {
          setError("Villa not found")
        }
      } catch (error) {
        console.error("Error fetching villa:", error)
        setError(`Failed to load villa: ${error.message}`)
      } finally {
        setIsLoading(false)
      }
    }

    if (!user) {
      navigate("/")
      return
    }

    fetchVilla()
  }, [id, user, isAdmin, navigate])

  useEffect(() => {
    if (formData.location?.lat && formData.location?.lng) {
      console.log("Map coordinates:", {
        lat: formData.location.lat,
        lng: formData.location.lng,
      })
    }
  }, [formData.location])

  const handleImageUpload = async (e, field) => {
    if (!isAdmin) {
      setError("Only administrators can modify property images");
      return;
    }

    const files = e.target.files;
    if (!files.length) return;

    const promises = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      let folderPath = "";

      // Match folder structure with AddPropertyPage
      switch (field) {
        case "images":
          folderPath = `villas/${id}/property-images`;
          break;
        case "thumbnail":
          folderPath = `villas/${id}/thumbnail`;
          break;
        case "locationMap":
          folderPath = `villas/${id}/location-maps`;
          break;
        case "meals.images":
          folderPath = `villas/${id}/meal-images`;
          break;
        case "locationImages":
          folderPath = `villas/${id}/location-images`;
          break;
        case "owner":
          folderPath = `villas/${id}/owner`;
          break;
        default:
          if (field.startsWith("management")) {
            folderPath = `villas/${id}/management`;
          }
          break;
      }

      const imageRef = storageRef(storage, `${folderPath}/${Date.now()}_${file.name}`);

      const uploadPromise = uploadBytes(imageRef, file)
        .then(async () => {
          const downloadURL = await getDownloadURL(imageRef);
          return { field, url: downloadURL };
        })
        .catch((error) => {
          console.error("Error uploading image:", error);
          setError(`Failed to upload image: ${error.message}`);
          return null;
        });

      promises.push(uploadPromise);
    }

    try {
      const results = await Promise.all(promises);
      results.forEach((result) => {
        if (!result) return;

        if (result.field === "meals.images") {
          setFormData((prev) => ({
            ...prev,
            meals: {
              ...prev.meals,
              images: [...(prev.meals.images || []), result.url],
            },
          }));
        } else if (result.field === "images") {
          setFormData((prev) => ({
            ...prev,
            images: [...prev.images, result.url],
          }));
        } else if (result.field === "owner") {
          setFormData((prev) => ({
            ...prev,
            owner: { ...prev.owner, image: result.url },
          }));
        } else if (result.field.startsWith("management")) {
          const index = Number.parseInt(result.field.split("-")[1]);
          setFormData((prev) => ({
            ...prev,
            management: prev.management.map((item, i) =>
              i === index ? { ...item, image: result.url } : item
            ),
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            [result.field]: result.url,
          }));
        }
      });
    } catch (error) {
      console.error("Error processing image uploads:", error);
      setError("Failed to process image uploads.");
    }
  }

  const handleLocationImagesUpload = async (e) => {
    if (!isAdmin) {
      setError("Only administrators can modify location images");
      return;
    }

    const files = e.target.files;
    if (!files.length) return;

    const promises = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const imageRef = storageRef(
        storage,
        `villas/${id}/location-images/${Date.now()}_${file.name}`
      );

      const uploadPromise = uploadBytes(imageRef, file)
        .then(async () => {
          const downloadURL = await getDownloadURL(imageRef);
          return downloadURL;
        })
        .catch((error) => {
          console.error("Error uploading location image:", error);
          return null;
        });

      promises.push(uploadPromise);
    }

    try {
      const urls = await Promise.all(promises);
      const validUrls = urls.filter((url) => url !== null);

      setFormData((prev) => ({
        ...prev,
        locationImages: [...(prev.locationImages || []), ...validUrls],
      }))
    } catch (error) {
      console.error("Error processing location images:", error)
      setError("Failed to upload location images.")
    }
  }

  const handleSpaceImageUpload = async (e, spaceIndex) => {
    if (!isAdmin) {
      setError("Only administrators can modify space images");
      return;
    }

    const files = e.target.files;
    if (!files.length) return;

    const file = files[0]; // Only use the first file
    const folderPath = `villas/${id}/spaces`;
    const imageRef = storageRef(storage, `${folderPath}/${Date.now()}_${file.name}`);

    try {
      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);

      // Update the space image
      setFormData(prev => {
        const updatedSpaces = [...prev.spaces];
        updatedSpaces[spaceIndex] = {
          ...updatedSpaces[spaceIndex],
          image: downloadURL
        };
        return {
          ...prev,
          spaces: updatedSpaces
        };
      });
    } catch (error) {
      console.error("Error uploading space image:", error);
      setError(`Failed to upload space image: ${error.message}`);
    }
  };

  const deleteImage = async (field, index) => {
    if (!isAdmin) {
      setError("Only administrators can delete property images");
      return;
    }

    try {
      let imageUrl = '';
      switch (field) {
        case "images":
          imageUrl = formData.images[index];
          break;
        case "thumbnail":
          imageUrl = formData.thumbnail;
          break;
        case "locationImages":
          imageUrl = formData.locationImages[index];
          break;
        case "meals.images":
          imageUrl = formData.meals?.images?.[index];
          break;
        case "owner":
          imageUrl = formData.owner.image;
          break;
        case "management":
          const managementIndex = Number.parseInt(field.split("-")[1]);
          imageUrl = formData.management[managementIndex].image;
          break;
        default:
          return;
      }

      if (imageUrl && typeof imageUrl === 'string' && (imageUrl.includes('cloud.appwrite.io') || imageUrl.startsWith('https://firebasestorage.googleapis.com'))) {
        // Delete from storage
        if (imageUrl.includes('cloud.appwrite.io') && imageUrl.includes('/files/')) {
          // Appwrite URL — extract file ID and delete
          const match = imageUrl.match(/\/files\/([^/]+)\//);
          if (match) {
            const imageRef = storageRef(storage, imageUrl);
            imageRef._fileId = match[1];
            await deleteObject(imageRef);
          }
        } else if (imageUrl.startsWith('https://firebasestorage.googleapis.com')) {
          // Legacy Firebase URL — skip deletion
          console.log('Skipping Firebase URL deletion (migrated)');
        }

        // Update the database
        const villaRef = ref(db, `villas/${id}`);

        if (field === "meals.images") {
          // Get current villa data
          const snapshot = await get(villaRef);
          const currentData = snapshot.val();
          const updatedMealImages = currentData.meals?.images?.filter((_, i) => i !== index) || [];

          // Update only the meals images array in the database
          await update(villaRef, {
            meals: {
              ...currentData.meals,
              images: updatedMealImages
            }
          });

          // Update local state
          setFormData(prev => ({
            ...prev,
            meals: {
              ...prev.meals,
              images: updatedMealImages
            }
          }));
        } else {
          // Handle other image types...
          setFormData(prev => {
            switch (field) {
              case "images":
                return { ...prev, images: prev.images.filter((_, i) => i !== index) };
              case "thumbnail":
                return { ...prev, thumbnail: null };
              case "locationImages":
                return { ...prev, locationImages: prev.locationImages.filter((_, i) => i !== index) };
              case "owner":
                return { ...prev, owner: { ...prev.owner, image: null } };
              case "management":
                const managementIndex = Number.parseInt(field.split("-")[1]);
                return {
                  ...prev,
                  management: prev.management.map((item, i) =>
                    i === managementIndex ? { ...item, image: null } : item
                  )
                };
              default:
                return prev;
            }
          });
        }

        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }

    } catch (error) {
      console.error("Error deleting image:", error);
      setError(`Failed to delete image: ${error.message}`);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!isAdmin && formData.ownerEmail !== user?.email) {
        throw new Error("You do not have permission to edit this property.");
      }

      // Check for valid coordinates
      const hasValidCoordinates =
        formData.location?.lat &&
        formData.location?.lng &&
        !isNaN(Number(formData.location.lat)) &&
        !isNaN(Number(formData.location.lng));

      // If no valid coordinates, try to extract from Maps link
      if (!hasValidCoordinates) {
        const coords = await extractCoordinates(formData.googleMapsLink);
        if (coords) {
          formData.location = coords;
        } else {
          throw new Error(
            "Please provide either valid coordinates (latitude and longitude) or a Google Maps link with location coordinates."
          );
        }
      }

      // Ensure coordinates are numbers
      const cleanFormData = {
        ...formData,
        updatedAt: Date.now(),
        location: {
          lat: Number(formData.location.lat),
          lng: Number(formData.location.lng)
        }
      };

      // Validate coordinate ranges
      if (
        cleanFormData.location.lat < -90 ||
        cleanFormData.location.lat > 90 ||
        cleanFormData.location.lng < -180 ||
        cleanFormData.location.lng > 180
      ) {
        throw new Error("Invalid coordinate values. Latitude must be between -90 and 90, and longitude between -180 and 180.");
      }

      const villaRef = ref(db, `villas/${id}`);
      await update(villaRef, cleanFormData);

      setSuccess(true);
      setTimeout(() => {
        if (isAdmin) {
          navigate("/admin");
        } else {
          navigate("/owner");
        }
      }, 2000);
    } catch (error) {
      console.error("Error updating property:", error);
      setError(`Failed to update property: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      if (name === "stationPickup" || name === "busStopPickup") {
        setFormData((prev) => ({
          ...prev,
          [name]: checked,
        }))
      } else if (name.startsWith("paid_")) {
        // Handle paid facility checkbox
        const facilityId = name.replace("paid_", "")
        setFormData((prev) => ({
          ...prev,
          facilities: prev.facilities.map((facility) => {
            if (typeof facility === 'string') {
              // Convert old string format to object format
              return facility === facilityId 
                ? { id: facilityId, isPaid: checked }
                : { id: facility, isPaid: false }
            } else {
              // Already object format
              return facility.id === facilityId 
                ? { ...facility, isPaid: checked }
                : facility
            }
          })
        }))
      } else {
        // Handle facility selection checkbox
        const facilityExists = formData.facilities.some(facility => 
          typeof facility === 'string' ? facility === name : facility.id === name
        )
        
        if (checked && !facilityExists) {
          // Add new facility
          setFormData((prev) => ({
            ...prev,
            facilities: [...prev.facilities, { id: name, isPaid: false }]
          }))
        } else if (!checked && facilityExists) {
          // Remove facility
          setFormData((prev) => ({
            ...prev,
            facilities: prev.facilities.filter(facility => 
              typeof facility === 'string' ? facility !== name : facility.id !== name
            )
          }))
        }
      }
    }
    // Update the googleMapsLink handling in handleChange
    else if (name === "googleMapsLink") {
      setFormData(prev => ({
        ...prev,
        googleMapsLink: value
      }));

      // Extract coordinates asynchronously
      extractCoordinates(value).then(coords => {
        if (coords) {
          setFormData(prev => ({
            ...prev,
            location: coords
          }));
        }
      }).catch(error => {
        console.error('Error handling Maps link:', error);
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }

  const handleLocationHighlight = (index, value) => {
    const newHighlights = [...formData.locationHighlights]
    newHighlights[index] = value
    setFormData((prev) => ({
      ...prev,
      locationHighlights: newHighlights,
    }))
  }

  const addLocationHighlight = () => {
    setFormData((prev) => ({
      ...prev,
      locationHighlights: [...prev.locationHighlights, ""],
    }))
  }

  const removeLocationHighlight = (index) => {
    setFormData((prev) => ({
      ...prev,
      locationHighlights: prev.locationHighlights.filter((_, i) => i !== index),
    }))
  }

  const handleManagementChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      management: prev.management.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }))
  }

  const addManagementMember = () => {
    setFormData((prev) => ({
      ...prev,
      management: [...prev.management, { name: "", image: null }],
    }))
  }

  const removeManagementMember = (index) => {
    setFormData((prev) => ({
      ...prev,
      management: prev.management.filter((_, i) => i !== index),
    }))
  }

  const handleCoordinateChange = (e, type) => {
    const value = Number.parseFloat(e.target.value)
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        [type]: value,
      },
    }))
  }

  const addSpace = () => {
    setFormData(prev => ({
      ...prev,
      spaces: [
        ...prev.spaces,
        {
          id: Date.now().toString(),
          name: "",
          topLabel: "",
          image: null,
          description: []
        }
      ]
    }));
  };

  const removeSpace = (index) => {
    setFormData(prev => ({
      ...prev,
      spaces: prev.spaces.filter((_, i) => i !== index)
    }));
  };

  const updateSpace = (index, field, value) => {
    setFormData(prev => {
      const updatedSpaces = [...prev.spaces];
      updatedSpaces[index] = {
        ...updatedSpaces[index],
        [field]: value
      };
      return {
        ...prev,
        spaces: updatedSpaces
      };
    });
  };

  const addSpaceDescription = (spaceIndex) => {
    setFormData(prev => {
      const updatedSpaces = [...prev.spaces];
      updatedSpaces[spaceIndex] = {
        ...updatedSpaces[spaceIndex],
        description: [...(updatedSpaces[spaceIndex].description || []), ""]
      };
      return {
        ...prev,
        spaces: updatedSpaces
      };
    });
  };

  const updateSpaceDescription = (spaceIndex, descIndex, value) => {
    setFormData(prev => {
      const updatedSpaces = [...prev.spaces];
      const updatedDesc = [...updatedSpaces[spaceIndex].description];
      updatedDesc[descIndex] = value;
      updatedSpaces[spaceIndex] = {
        ...updatedSpaces[spaceIndex],
        description: updatedDesc
      };
      return {
        ...prev,
        spaces: updatedSpaces
      };
    });
  };

  const removeSpaceDescription = (spaceIndex, descIndex) => {
    setFormData(prev => {
      const updatedSpaces = [...prev.spaces];
      updatedSpaces[spaceIndex] = {
        ...updatedSpaces[spaceIndex],
        description: updatedSpaces[spaceIndex].description.filter((_, i) => i !== descIndex)
      };
      return {
        ...prev,
        spaces: updatedSpaces
      };
    });
  };

  // Add this function near the top of your component
  const extractCoordinates = async (url) => {
    if (!url) return null;

    try {
      // First try to expand short URL if it's a maps.app.goo.gl link
      if (url.includes('maps.app.goo.gl')) {
        const response = await fetch(url);
        const fullUrl = response.url;
        // Now work with the expanded URL
        url = fullUrl;
      }

      // Try different coordinate extraction patterns
      let coords = null;

      // Pattern 1: Standard maps link with @lat,lng
      let match = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (match) {
        return {
          lat: parseFloat(match[1]),
          lng: parseFloat(match[2])
        };
      }

      // Pattern 2: Place ID URL
      if (url.includes('/place/')) {
        const placeIdMatch = url.match(/place\/([^\/]+)/);
        if (placeIdMatch) {
          const placeName = decodeURIComponent(placeIdMatch[1]);
          // Try to find coordinates after place name
          const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
          if (coordMatch) {
            return {
              lat: parseFloat(coordMatch[1]),
              lng: parseFloat(coordMatch[2])
            };
          }
        }
      }

      // Pattern 3: Maps coordinates in !3d!4d format
      const lat3d = url.match(/!3d(-?\d+\.\d+)/);
      const lng4d = url.match(/!4d(-?\d+\.\d+)/);
      if (lat3d && lng4d) {
        return {
          lat: parseFloat(lat3d[1]),
          lng: parseFloat(lng4d[1])
        };
      }

      // Pattern 4: Query parameter format
      const searchParams = new URL(url).searchParams;
      const queryCoords = searchParams.get('q')?.match(/(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (queryCoords) {
        return {
          lat: parseFloat(queryCoords[1]),
          lng: parseFloat(queryCoords[2])
        };
      }

      return null;
    } catch (error) {
      console.error('Error extracting coordinates:', error);
      return null;
    }
  };

  const getFacilityStatus = (facilityId) => {
    const facility = formData.facilities.find(f => 
      typeof f === 'string' ? f === facilityId : f.id === facilityId
    )
    if (!facility) return { selected: false, isPaid: false }
    if (typeof facility === 'string') return { selected: true, isPaid: false }
    return { selected: true, isPaid: facility.isPaid || false }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
        <span className="ml-2">Loading...</span>
      </div>
    )
  }

  if (!user || (!isAdmin && formData.ownerEmail !== user.email)) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p>You must be the owner or an admin to edit this property.</p>
          <Link to="/" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
            Return to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-black text-white p-4 sticky top-0 z-10 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          &lt; Back
        </Button>
        <div className="container mx-auto px-4 lg:px-8">
          <h1 className="text-xl font-bold">Edit Property</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto bg-white p-4 sm:p-8 rounded-lg shadow">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-6 bg-green-50 text-green-800 border-green-200">
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>Property updated successfully! Redirecting...</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Basic Information</h2>

              <div>
                <Label htmlFor="name">Property Name</Label>
                <Input id="name" name="name" value={formData.name} onChange={handleChange} required />
              </div>

              <div className="mb-6">
                <Label>Thumbnail Image</Label>
                {isAdmin ? (
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "thumbnail")}
                    className="mt-2"
                  />
                ) : (
                  <p className="text-sm text-gray-500 mt-1">Contact administrator to modify property images WhatsApp (+91 8668737989)</p>
                )}
                {formData.thumbnail && (
                  <div className="mt-2 relative">
                    <img
                      src={formData.thumbnail || "/placeholder.svg"}
                      alt="Thumbnail"
                      className="w-32 h-32 object-cover rounded"
                    />
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => deleteImage("thumbnail")}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="mainCity">Main City</Label>
                <Input id="mainCity" name="mainCity" value={formData.mainCity} onChange={handleChange} required />
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea id="address" name="address" value={formData.address} onChange={handleChange} required />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="guestCount">Guest Count</Label>
                  <Input
                    id="guestCount"
                    name="guestCount"
                    type="number"
                    value={formData.guestCount}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="bedrooms">Bedrooms</Label>
                  <Input 
                    id="bedrooms"
                    name="bedrooms"
                    type="number"
                    value={formData.bedrooms}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label htmlFor="halls">Living/Dining Halls</Label>
                  <Input 
                    id="halls"
                    name="halls"
                    type="number"
                    value={formData.halls}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label htmlFor="kitchens">Kitchens</Label>
                  <Input 
                    id="kitchens"
                    name="kitchens"
                    type="number"
                    value={formData.kitchens}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label htmlFor="totalVillaArea">Total Villa Area (m²)</Label>
                  <Input
                    id="totalVillaArea"
                    name="totalVillaArea"
                    type="number"
                    value={formData.totalVillaArea}
                    onChange={handleChange}
                    placeholder="Optional: Enter villa area if known"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="rate">Rate (per night)</Label>
                <Input id="rate" name="rate" type="number" value={formData.rate} onChange={handleChange} required />
              </div>

{/* Add Pricing Type Toggle */}
              <div className="space-y-3">
                <Label>Pricing Type</Label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${formData.pricingType === 'fullNight' ? 'text-blue-600' : 'text-gray-500'}`}>
                      Full Night
                    </span>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, pricingType: formData.pricingType === 'fullNight' ? 'perPerson' : 'fullNight' }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        formData.pricingType === 'perPerson' ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.pricingType === 'perPerson' ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`text-sm font-medium ${formData.pricingType === 'perPerson' ? 'text-blue-600' : 'text-gray-500'}`}>
                      Per Person
                    </span>
                  </div>
                </div>
                
                {/* Manual Guest Count for Per Person Calculation */}
                {formData.pricingType === 'perPerson' && (
                  <div className="mt-3">
                    <Label htmlFor="perPersonGuestCount">Guest Count for Per Person Calculation</Label>
                    <Input
                      id="perPersonGuestCount"
                      name="perPersonGuestCount"
                      type="number"
                      value={formData.perPersonGuestCount || formData.guestCount || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, perPersonGuestCount: e.target.value }))}
                      placeholder="Enter guest count for calculation"
                      className="max-w-xs"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      This will be used to calculate the per-person rate. You can set it different from the maximum guest capacity.
                    </p>
                  </div>
                )}
                
                {/* Price Display */}
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                  <div className="text-sm text-gray-600 mb-1">Price Display:</div>
                  {formData.pricingType === 'fullNight' ? (
                    <div className="text-lg font-semibold text-gray-900">
                      ₹{formData.rate || '0'} per night
                    </div>
                  ) : (
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        ₹{formData.rate || '0'} per night
                      </div>
                      {formData.rate && (formData.perPersonGuestCount || formData.guestCount) && (
                        <div className="text-sm text-blue-600 font-medium">
                          ₹{Math.round((parseFloat(formData.rate) / parseFloat(formData.perPersonGuestCount || formData.guestCount)) || 0)} per person
                          <span className="text-gray-500 ml-1">
                            (based on {formData.perPersonGuestCount || formData.guestCount} guests)
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-gray-500">
                  {formData.pricingType === 'fullNight' 
                    ? 'Shows the total price for the entire villa per night'
                    : 'Shows both total price and calculated per-person rate based on your specified guest count'
                  }
                </p>
              </div>
            </div>

            {/* Property Images */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Property Images</h2>

              <div className="mb-6">
                <Label>Property Images</Label>
                {isAdmin ? (
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageUpload(e, "images")}
                    className="mt-2"
                  />
                ) : (
                  <p className="text-sm text-gray-500 mt-1">Contact administrator to modify property images WhatsApp (+91 8668737989)</p>
                )}
                <div className="grid grid-cols-3 gap-4 mt-2">
                  {formData.images?.map((url, index) => (
                    <div key={index} className="relative">
                      <img src={url || "/placeholder.svg"} alt={`Property ${index + 1}`} className="w-full h-32 object-cover rounded" />
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => deleteImage("images", index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* About This Place */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">About This Place</h2>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={8}
                  placeholder="Enter property description. Press Enter for new paragraphs."
                  className="whitespace-pre-line"
                />
              </div>

              <div>
                <Label htmlFor="architecturalUniqueness">Architectural Uniqueness</Label>
                <Textarea
                  id="architecturalUniqueness"
                  name="architecturalUniqueness"
                  value={formData.architecturalUniqueness || ""}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Describe any unique architectural features (optional)"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave blank if not applicable. This section will only appear on the villa detail page if filled.
                </p>
              </div>
            </div>

            {/* Meals Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Meals</h2>

              <div className="space-y-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.meals.available}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      meals: {
                        ...prev.meals,
                        available: e.target.checked
                      }
                    }))}
                    className="rounded border-gray-300"
                  />
                  <span>Meal Service Available</span>
                </label>

                {formData.meals.available && (
                  <>
                    <div>
                      <Label>Meal Types</Label>
                      <div className="flex flex-wrap gap-4 mt-2">
                        {['veg', 'nonveg', 'vegan'].map(type => (
                          <label key={type} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.meals.types.includes(type)}
                              onChange={(e) => {
                                const newTypes = e.target.checked
                                  ? [...formData.meals.types, type]
                                  : formData.meals.types.filter(t => t !== type)
                                setFormData(prev => ({
                                  ...prev,
                                  meals: {
                                    ...prev.meals,
                                    types: newTypes
                                  }
                                }))
                              }}
                              className="rounded border-gray-300"
                            />
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6">
                                {type === 'veg' && (
                                  <svg viewBox="0 0 24 24">
                                    <rect width="20" height="20" x="2" y="2" fill="none" stroke="green" strokeWidth="2" />
                                    <circle cx="12" cy="12" r="6" fill="green" />
                                  </svg>
                                )}
                                {type === 'nonveg' && (
                                  <svg viewBox="0 0 24 24">
                                    <rect width="20" height="20" x="2" y="2" fill="none" stroke="red" strokeWidth="2" />
                                    <circle cx="12" cy="12" r="6" fill="red" />
                                  </svg>
                                )}
                                {type === 'vegan' && (
                                  <svg viewBox="0 0 24 24">
                                    <rect width="20" height="20" x="2" y="2" fill="none" stroke="darkgreen" strokeWidth="2" />
                                    <path d="M12 6l-6 12h12z" fill="darkgreen" />
                                  </svg>
                                )}
                              </div>
                              <span className="capitalize">{type}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="meal-description">Description</Label>
                      <Textarea
                        id="meal-description"
                        value={formData.meals.description}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          meals: {
                            ...prev.meals,
                            description: e.target.value
                          }
                        }))}
                        placeholder="Describe your meal service (e.g., We provide healthy home-cooked meals including breakfast, lunch, and dinner)"
                        rows={3}
                      />
                    </div>

                    <div className="mb-6">
                      <Label>Menu Card Images</Label>
                      {isAdmin ? (
                        <Input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => handleImageUpload(e, "meals.images")}
                          className="mt-2"
                        />
                      ) : (
                        <p className="text-sm text-gray-500 mt-1">Contact administrator to modify menu images WhatsApp (+91 8668737989)</p>
                      )}
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {formData.meals?.images?.filter(Boolean).map((image, index) => (
                          <div key={index} className="relative aspect-[3/4]">
                            <img
                              src={image || "/placeholder.svg"}
                              alt={`Menu ${index + 1}`}
                              className="absolute inset-0 w-full h-full object-cover rounded"
                              onError={(e) => {
                                e.target.src = "/placeholder.svg";
                                e.target.onerror = null;
                              }}
                            />
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => deleteImage("meals.images", index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Facilities */}
            
<div className="space-y-4">
  <h2 className="text-xl font-semibold">Facilities</h2>
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto p-4 border rounded-lg">
    {facilityOptions.map((facility) => {
      const facilityStatus = getFacilityStatus(facility.id)
      return (
        <div
          key={facility.id}
          className="p-3 border rounded-lg hover:bg-gray-50 transition-colors"
        >
          <label className="flex items-center space-x-3 cursor-pointer mb-2">
            <input
              type="checkbox"
              name={facility.id}
              checked={facilityStatus.selected}
              onChange={handleChange}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <facility.icon />
              </div>
              <span className="text-sm">{facility.label}</span>
            </div>
          </label>
          
          {facilityStatus.selected && (
            <label className="flex items-center space-x-2 ml-7 cursor-pointer">
              <input
                type="checkbox"
                name={`paid_${facility.id}`}
                checked={facilityStatus.isPaid}
                onChange={handleChange}
                className="w-3 h-3 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-xs text-green-600 font-medium">Paid Service (₹)</span>
            </label>
          )}
        </div>
      )
    })}
  </div>
</div>

            {/* Location Details */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Location Details</h2>

              <div>
                <Label htmlFor="googleMapsLink">Google Maps Link</Label>
                <div className="flex gap-2">
                  <Input
                    id="googleMapsLink"
                    name="googleMapsLink"
                    type="url"
                    value={formData.googleMapsLink}
                    onChange={handleChange}
                    placeholder="e.g., https://maps.app.goo.gl/..."
                    className="flex-1"
                  />
                  <a
                    href="https://www.google.com/maps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Open Maps
                  </a>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Paste a Google Maps link or manually enter coordinates below
                </p>
              </div>

              {/* Add manual coordinate inputs */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={formData.location?.lat || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      location: {
                        ...prev.location,
                        lat: parseFloat(e.target.value)
                      }
                    }))}
                    placeholder="e.g., 19.0760"
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={formData.location?.lng || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      location: {
                        ...prev.location,
                        lng: parseFloat(e.target.value)
                      }
                    }))}
                    placeholder="e.g., 72.8777"
                    className="w-full"
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500">
                You can enter coordinates manually if Google Maps link extraction fails.
                Find coordinates on Google Maps by right-clicking a location and selecting "What's here?"
              </p>
            </div>

            {/* About The Location */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">About The Location</h2>

              <div>
                <Label>Location Highlights</Label>
                {formData.locationHighlights.map((highlight, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={highlight}
                      onChange={(e) => handleLocationHighlight(index, e.target.value)}
                      placeholder="Add a location highlight"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => removeLocationHighlight(index)}
                    >
                      ×
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addLocationHighlight} className="mt-2">
                  Add Highlight
                </Button>
              </div>

              <div className="mb-6">
                <Label>Location Images</Label>
                {isAdmin ? (
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleLocationImagesUpload}
                    className="mt-2"
                  />
                ) : (
                  <p className="text-sm text-gray-500 mt-1">Contact administrator to modify location images WhatsApp (+91 8668737989)</p>
                )}
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {formData.locationImages?.map((image, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={image || "/placeholder.svg"}
                        alt={`Location ${index + 1}`}
                        className="absolute inset-0 w-full h-full object-cover rounded"
                      />
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => deleteImage("locationImages", index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Transportation */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Transportation</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="trainDistance">Distance to Nearest Train Station (km)</Label>
                  <Input
                    id="trainDistance"
                    name="trainDistance"
                    type="number"
                    step="0.1"
                    value={formData.trainDistance}
                    onChange={handleChange}
                    placeholder="e.g., 2.5"
                  />
                </div>
                <div>
                  <Label htmlFor="busDistance">Distance to Nearest Bus Stop (km)</Label>
                  <Input
                    id="busDistance"
                    name="busDistance"
                    type="number"
                    step="0.1"
                    value={formData.busDistance}
                    onChange={handleChange}
                    placeholder="e.g., 1.2"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="stationPickup"
                      checked={formData.stationPickup}
                      onChange={handleChange}
                      className="rounded border-gray-300"
                    />
                    <span>Station Pickup/Drop Service Available</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="busStopPickup"
                      checked={formData.busStopPickup}
                      onChange={handleChange}
                      className="rounded border-gray-300"
                    />
                    <span>Bus Stop Pickup/Drop Service Available</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Villa Rules */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Villa Rules</h2>
              <div>
                <Label htmlFor="rules">Rules</Label>
                <Textarea
                  id="rules"
                  name="rules"
                  value={formData.rules ? formData.rules.join("\n") : ""}
                  onChange={(e) => {
                    const rules = e.target.value.split("\n").filter((item) => item.trim() !== "")
                    setFormData((prevData) => ({
                      ...prevData,
                      rules: rules,
                    }))
                  }}
                  placeholder="Enter each rule on a new line"
                  rows={4}
                />
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Cancellation Policy</h2>
              <div>
                <Label htmlFor="cancellationPolicy">Cancellation Policy</Label>
                <Textarea
                  id="cancellationPolicy"
                  name="cancellationPolicy"
                  value={formData.cancellationPolicy || ""}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Describe your cancellation policy (optional)"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave blank if not applicable. This section will only appear on the villa detail page if filled.
                </p>
              </div>
            </div>


            {/* Management */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Management</h2>
              {formData.management.map((member, index) => (
                <div key={index} className="space-y-2 p-4 border rounded">
                  <div>
                    <Label htmlFor={`management-${index}-name`}>Name</Label>
                    <Input
                      id={`management-${index}-name`}
                      value={member.name}
                      onChange={(e) => handleManagementChange(index, "name", e.target.value)}
                    />
                  </div>
                  <div className="mb-6">
                    <Label>Management Team</Label>
                    {formData.management.map((member, index) => (
                      <div key={index} className="mb-4">
                        <Input
                          type="text"
                          value={member.name}
                          onChange={(e) => handleManagementChange(index, "name", e.target.value)}
                          placeholder="Name"
                          className="mb-2"
                        />
                        {isAdmin ? (
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, `management.${index}`)}
                            className="mt-2"
                          />
                        ) : (
                          <p className="text-sm text-gray-500 mt-1">Contact administrator to modify management images WhatsApp (+91 8668737989)</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="destructive" onClick={() => removeManagementMember(index)}>
                    Remove
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addManagementMember}>
                Add Management Member
              </Button>
            </div>

            {/* Owner Information */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Owner Information</h2>
              <div>
                <Label htmlFor="owner-name">Owner Name</Label>
                <Input
                  id="owner-name"
                  value={formData.owner.name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      owner: { ...prev.owner, name: e.target.value },
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="ownerEmail">Owner Email</Label>
                <Input
                  id="ownerEmail"
                  name="ownerEmail"
                  type="email"
                  value={formData.ownerEmail}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="upiId">UPI ID</Label>
                <Input
                  id="upiId"
                  name="upiId"
                  value={formData.upiId}
                  onChange={handleChange}
                  placeholder="e.g., yourname@upi"
                />
              </div>
              <div>
                <Label htmlFor="owner-image">Owner Image</Label>
                <Input id="owner-image" type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "owner")} />
                {formData.owner.image && (
                  <div className="relative mt-2">
                    <img
                      src={formData.owner.image || "/placeholder.svg"}
                      alt="Owner"
                      className="mt-2 h-24 w-24 object-cover rounded"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 rounded-full h-6 w-6 p-0"
                      onClick={() => deleteImage("owner")}
                    >
                      ×
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Social Links */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Social Links</h2>
              <div>
                <Label htmlFor="youtubeLink">YouTube Link</Label>
                <Input id="youtubeLink" name="youtubeLink" value={formData.youtubeLink} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="instagramProfile">Instagram Profile</Label>
                <Input
                  id="instagramProfile"
                  name="instagramProfile"
                  value={formData.instagramProfile}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="whatsappNumber">WhatsApp Number</Label>
                <Input
                  id="whatsappNumber"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* YouTube Shorts Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">YouTube Shorts</h2>

              <div>
                <Label>Featured Shorts</Label>
                {formData.youtubeShorts.map((short, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={short}
                      onChange={(e) => {
                        const newShorts = [...formData.youtubeShorts];
                        newShorts[index] = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          youtubeShorts: newShorts
                        }));
                      }}
                      placeholder="Enter YouTube Short URL or ID"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          youtubeShorts: prev.youtubeShorts.filter((_, i) => i !== index)
                        }));
                      }}
                    >
                      ×
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      youtubeShorts: [...prev.youtubeShorts, ""]
                    }));
                  }}
                  className="mt-2"
                >
                  Add Short
                </Button>
              </div>
            </div>

            {/* Custom Pricing Categories Section */}
            <div className="space-y-4 border p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Custom Pricing Categories</h3>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="enableCustomPricing" className="cursor-pointer">
                    Enable Custom Pricing
                  </Label>
                  <input
                    type="checkbox"
                    id="enableCustomPricing"
                    checked={formData.pricing?.enabled || false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        pricing: {
                          ...prev.pricing,
                          enabled: e.target.checked,
                        },
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </div>
              </div>

              {formData.pricing?.enabled && (
                <div className="space-y-4">
                  {formData.pricing.categories.map((category, index) => (
                    <div key={index} className="flex gap-4 items-start">
                      <div className="flex-1">
                        <Label htmlFor={`category-${index}-heading`}>Category</Label>
                        <Input
                          id={`category-${index}-heading`}
                          value={category.heading}
                          onChange={(e) => {
                            const newCategories = [...formData.pricing.categories]
                            newCategories[index].heading = e.target.value
                            setFormData((prev) => ({
                              ...prev,
                              pricing: {
                                ...prev.pricing,
                                categories: newCategories
                              }
                            }))
                          }}
                          placeholder="e.g., Saturday (8 persons)"
                        />
                      </div>
                      <div className="w-1/3">
                        <Label htmlFor={`category-${index}-price`}>Price</Label>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">₹</span>
                          <Input
                            id={`category-${index}-price`}
                            type="number"
                            value={category.price}
                            onChange={(e) => {
                              const newCategories = [...formData.pricing.categories]
                              newCategories[index].price = e.target.value
                              setFormData((prev) => ({
                                ...prev,
                                pricing: {
                                  ...prev.pricing,
                                  categories: newCategories
                                }
                              }))
                            }}
                            placeholder="0"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="mt-8"
                        onClick={() => {
                          const newCategories = formData.pricing.categories.filter((_, i) => i !== index)
                          setFormData((prev) => ({
                            ...prev,
                            pricing: {
                              ...prev.pricing,
                              categories: newCategories
                            }
                          }))
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        pricing: {
                          ...prev.pricing,
                          categories: [...prev.pricing.categories, { heading: "", price: "" }]
                        }
                      }))
                    }}
                  >
                    Add Price Category
                  </Button>

                  <div>
                    <Label htmlFor="pricing-note">Pricing Note (Optional)</Label>
                    <Textarea
                      id="pricing-note"
                      value={formData.pricing?.note || ""}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          pricing: {
                            ...prev.pricing,
                            note: e.target.value
                          }
                        }))
                      }}
                      placeholder="Add any additional notes about pricing..."
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Pool Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="poolCount">Number of Pools</Label>
                <Input
                  id="poolCount"
                  name="poolCount"
                  type="number"
                  value={formData.poolCount}
                  onChange={handleChange}
                  placeholder="Enter number of pools"
                />
              </div>

              <div>
                <Label htmlFor="poolSize">Pool Size (m²)</Label>
                <Input
                  id="poolSize"
                  name="poolSize"
                  type="number"
                  value={formData.poolSize}
                  onChange={handleChange}
                  placeholder="Enter pool size"
                />
              </div>
            </div>

            {/* Check-in/Check-out Times */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Check-in/Check-out Times</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="checkInTime">Check-in Time</Label>
                  <Input
                    id="checkInTime"
                    name="checkInTime"
                    type="time"
                    value={formData.checkInTime}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="checkOutTime">Check-out Time</Label>
                  <Input
                    id="checkOutTime"
                    name="checkOutTime"
                    type="time"
                    value={formData.checkOutTime}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Spaces Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Spaces</h2>
              <p className="text-sm text-gray-500">Add individual spaces like bedrooms, bathrooms, etc.</p>

              {formData.spaces.map((space, index) => (
                <div key={space.id || index} className="border p-4 rounded-lg space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Space {index + 1}</h3>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => removeSpace(index)}
                    >
                      Remove
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`space-${index}-name`}>Name (Bottom Left Label)</Label>
                      <Input
                        id={`space-${index}-name`}
                        value={space.name}
                        onChange={(e) => updateSpace(index, "name", e.target.value)}
                        placeholder="e.g., Bedroom 1"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`space-${index}-topLabel`}>Top Right Label</Label>
                      <Input
                        id={`space-${index}-topLabel`}
                        value={space.topLabel}
                        onChange={(e) => updateSpace(index, "topLabel", e.target.value)}
                        placeholder="e.g., King-size bed"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Space Image</Label>
                    <div className="mt-2">
                      <div className="grid grid-cols-4 gap-4 max-h-[300px] overflow-y-auto p-2 border rounded-lg">
                        <div
                          className={`relative aspect-square cursor-pointer rounded-lg border-2 ${!space.image ? 'border-blue-500' : 'border-transparent'
                            }`}
                          onClick={() => updateSpace(index, "image", null)}
                        >
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                            <span className="text-gray-500">No Image</span>
                          </div>
                        </div>
                        {formData.images.map((image, imgIndex) => (
                          <div
                            key={imgIndex}
                            className={`relative aspect-square cursor-pointer rounded-lg border-2 ${space.image === image ? 'border-blue-500' : 'border-transparent'
                              }`}
                            onClick={() => updateSpace(index, "image", image)}
                          >
                            <img
                              src={image || "/placeholder.svg"}
                              alt={`Property ${imgIndex + 1}`}
                              className="absolute inset-0 w-full h-full object-cover rounded-lg"
                            />
                            {space.image === image && (
                              <div className="absolute inset-0 bg-blue-500 bg-opacity-20 rounded-lg">
                                <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    {space.image && (
                      <div className="mt-4">
                        <Label>Selected Image Preview</Label>
                        <div className="mt-2 relative w-40 h-40">
                          <img
                            src={space.image || "/placeholder.svg"}
                            alt={space.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => updateSpace(index, "image", null)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Description Points</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addSpaceDescription(index)}
                      >
                        Add Point
                      </Button>
                    </div>

                    {space.description?.map((desc, descIndex) => (
                      <div key={descIndex} className="flex gap-2 mb-2">
                        <Input
                          value={desc}
                          onChange={(e) => updateSpaceDescription(index, descIndex, e.target.value)}
                          placeholder="Add a description point"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => removeSpaceDescription(index, descIndex)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={addSpace}
                className="w-full py-2"
              >
                Add Space
              </Button>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}